import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaGithub, FaLinkedin, FaTwitter, FaPaperPlane, FaWhatsapp, FaInstagram, FaFacebook } from 'react-icons/fa';
import Button from '../components/Button';
import axios from "axios";
import { toast } from "react-toastify";


const ContactSection = styled.section`
  padding: 5rem 0;
  background: #0a0a0a;
`;

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 3rem;
  
  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
    margin-bottom: 1rem;
    color: #ffffff;
  }
  
  p {
    font-size: 1.2rem;
    color: #cccccc;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfo = styled(motion.div)`
  h3 {
    font-size: 1.8rem;
    margin-bottom: 2rem;
    color: #ffffff;
  }
`;

const ContactMethods = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 3rem;
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  
  .icon {
    color: #4682B4;
    font-size: 1.5rem;
    margin-top: 0.25rem;
  }
  
  .content {
    flex: 1;
    
    h4 {
      font-size: 1.2rem;
      margin-bottom: 0.5rem;
      color: #ffffff;
    }
    
    p {
      color: #cccccc;
      line-height: 1.6;
    }
  }
`;

const SocialLinks = styled.div`
  h4 {
    font-size: 1.2rem;
    margin-bottom: 1rem;
    color: #ffffff;
  }
  
  .social-grid {
    display: flex;
    gap: 1rem;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: #1a1a1a;
  border-radius: 50%;
  color: #4682B4;
  font-size: 1.3rem;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(70, 130, 180, 0.3);
  border: 1px solid rgba(70, 130, 180, 0.1);
  
  &:hover {
    background: #87CEEB;
    color: #000000;
    transform: translateY(-3px);
    border-color: rgba(70, 130, 180, 0.3);
  }
`;

const ContactForm = styled(motion.div)`
  background: #1a1a1a;
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(70, 130, 180, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #ffffff;
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid #333333;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #2a2a2a;
  color: #ffffff;
  
  &:focus {
    outline: none;
    border-color: #4682B4;
    box-shadow: 0 0 0 3px rgba(70, 130, 180, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 2px solid #333333;
  border-radius: 10px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  background: #2a2a2a;
  color: #ffffff;
  
  &:focus {
    outline: none;
    border-color: #4682B4;
    box-shadow: 0 0 0 3px rgba(70, 130, 180, 0.1);
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const SuccessMessage = styled(motion.div)`
  background: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 1rem;
  border: 1px solid #c3e6cb;
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      console.log('Sending form data:', Object.fromEntries(formDataToSend));
      const response = await axios.post(
        "https://softvancesolution.com/Backend/SendMail.php",
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.res) {
        toast.success(response.data.res);

        setIsSubmitted(true);

        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });

        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        toast.error("Something went wrong");
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error sending message: ' + error.message + '. Please try again or contact directly via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: <FaEnvelope style={{ color: '#4285F4' }} />,
      title: "Email",
      info: "chqasimfarooq13@gmail.com",
      description: "Send me an email anytime. I'll respond as soon as possible."
    },
    {
      icon: <FaPhone style={{ color: '#DC143C' }} />,
      title: "Phone",
      info: "03095089454",
      description: "Available for calls Monday-Friday, 9AM-6PM."
    },
    {
      icon: <FaWhatsapp style={{ color: '#25D366' }} />,
      title: "WhatsApp",
      info: "03095089454",
      description: "Reach out on WhatsApp for quick responses and project discussions."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <ContactSection>
      <ContactContainer>
        <SectionHeader
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2>Get In Touch</h2>
          <p>
            Have a project in mind or want to discuss how I can help your business?
            I'd love to hear from you. Send me a message and I'll get back to you soon.
          </p>
        </SectionHeader>

        <ContactGrid>
          <ContactInfo
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h3 variants={itemVariants}>Let's Connect</motion.h3>

            <ContactMethods>
              {contactMethods.map((method, index) => (
                <ContactMethod
                  key={index}
                  variants={itemVariants}
                >
                  <div className="icon">{method.icon}</div>
                  <div className="content">
                    <h4>{method.title}</h4>
                    <p>{method.info}</p>
                    <p>{method.description}</p>
                  </div>
                </ContactMethod>
              ))}
            </ContactMethods>

            <SocialLinks variants={itemVariants}>
              <h4>Follow Me</h4>
              <div className="social-grid">
                <SocialLink href="https://github.com" target="_blank" rel="noopener noreferrer">
                  <FaGithub style={{ color: '#333333' }} />
                </SocialLink>
                <SocialLink href="https://www.instagram.com/softvancesolutions/" target="_blank" rel="noopener noreferrer">
                  <FaInstagram style={{ color: '#E4405F' }} />
                </SocialLink>
                <SocialLink href="https://www.linkedin.com/in/softvancesol/" target="_blank" rel="noopener noreferrer">
                  <FaLinkedin style={{ color: '#0077B5' }} />
                </SocialLink>
              </div>
            </SocialLinks>
          </ContactInfo>

          <ContactForm
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {isSubmitted && (
              <SuccessMessage
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                Thank you for your message! I'll get back to you soon.
              </SuccessMessage>
            )}

            <form onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel htmlFor="name">Name *</FormLabel>
                <FormInput
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  disabled={isSubmitting}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="email">Email *</FormLabel>
                <FormInput
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  disabled={isSubmitting}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="subject">Subject *</FormLabel>
                <FormInput
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Project Inquiry"
                  disabled={isSubmitting}
                />
                {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="message">Message *</FormLabel>
                <FormTextarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me about your project..."
                  disabled={isSubmitting}
                />
                {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
              </FormGroup>

              <Button
                type="submit"
                disabled={isSubmitting}
                style={{ width: '100%' }}
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message <FaPaperPlane style={{ marginLeft: '0.5rem' }} />
                  </>
                )}
              </Button>
            </form>
          </ContactForm>
        </ContactGrid>
      </ContactContainer>
    </ContactSection>
  );
};

export default Contact;
